import { chakra, Flex, Icon, Text } from '@chakra-ui/react'
import { Pause, Play } from 'lucide-react'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import useTranscriptionLogContext from '~/pages/CallAnalytics/Transcription/context/TranscriptionLogContext'
import { formatMilliseconds, tripLeadingZeroFromTime } from '~/utils/formatter'

import { toNounCasing } from '@mangovoice/core'
import { keyboardFocusRing, textStyleKeys } from '@mangovoice/design-system'
import { textStyles } from '@mangovoice/design-system'

interface TranscriptEntryHeaderProps {
  transcriptId: string
  personId: number
  personName: string
  beginOffsetMillis: number
  isActive: boolean
  isHovered: boolean
  isPlaying: boolean
}
const TranscriptEntryHeader = ({
  personName,
  beginOffsetMillis,
  isActive,
  isHovered,
  isPlaying,
}: TranscriptEntryHeaderProps) => {
  const { t } = useTranslation()
  const [wasActive, setWasActive] = useState<boolean>()
  const { onPlay, onPause } = useTranscriptionLogContext()

  //keep track of whether or not this component was active to control animation states
  useEffect(() => {
    if (isActive) {
      setWasActive(true)
    }

    return () => {
      setWasActive(undefined)
    }
  }, [isActive])

  //reset wasActive so animations don't trigger on subsequent rerenders from things like hover state changes
  useEffect(() => {
    if (wasActive && !isActive) {
      setWasActive(false)
    }
  }, [wasActive, isActive])

  const jsx = useMemo(() => {
    const handleClick = () => {
      if (isActive) {
        if (isPlaying) {
          onPause?.()
        } else {
          onPlay?.()
        }
      } else {
        onPlay?.(beginOffsetMillis)
      }
    }

    const time = tripLeadingZeroFromTime(formatMilliseconds(beginOffsetMillis))
    const speaker = toNounCasing(personName)
    const ariaLabel =
      isActive && isPlaying
        ? t(
            'callAnalytics.transcript.pauseFrom',
            'Pause from {{time}}, {{speaker}}',
            {
              time,
              speaker,
            },
          )
        : t(
            'callAnalytics.transcript.playFrom',
            'Play from {{time}}, {{speaker}}',
            {
              time,
              speaker,
            },
          )

    return (
      <Flex flex="1" height="25px" alignItems="flex-end" gap="0.5rem">
        <chakra.button
          type="button"
          display="flex"
          aria-label={ariaLabel}
          onClick={handleClick}
          {...keyboardFocusRing}
          _focusVisible={
            isActive ? undefined : { '& svg': { color: 'fg.default' } }
          }
          css={{
            alignItems: 'center',
            cursor: 'pointer',
            minWidth: '56px',
            padding: '2px 6px',
            justifyContent: 'space-between',
            borderRadius: '4px',
            backgroundColor: isActive
              ? 'var(--chakra-colors-brand-muted)'
              : isHovered
                ? 'var(--chakra-colors-brand-subtle)'
                : 'var(--chakra-colors-bg-panel)',
          }}
        >
          <Text
            as="span"
            {...textStyles[textStyleKeys.Body4_Bold]}
            color={
              isActive ? 'brand.fg' : isHovered ? 'fg.default' : 'fg.muted'
            }
          >
            {time}
          </Text>
          <Icon
            color={
              isActive ? 'brand.fg' : isHovered ? 'fg.default' : 'transparent'
            }
            height="8px"
            width="7px"
          >
            {isActive && isPlaying ? (
              <Pause size="100%" />
            ) : (
              <Play size="100%" />
            )}
          </Icon>
        </chakra.button>
        <Text
          {...textStyles[textStyleKeys.Body4_Bold]}
          color={isHovered || isActive ? 'fg.default' : 'fg.muted'}
        >
          {speaker}
        </Text>
      </Flex>
    )
  }, [
    isActive,
    isHovered,
    isPlaying,
    beginOffsetMillis,
    onPlay,
    onPause,
    personName,
    t,
  ])

  return jsx
}

export default TranscriptEntryHeader
